import { ITheme, IStyle, mergeStyleSets, getTheme, PanelType } from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IProductDetailPanelDisplayProps, ProductDetailPanelContent } from './content';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { ProductDetailPanelFooter } from './footer';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';
import { BwjConscientiousContainer } from '../../../../framework/components/bwj-conscientious-container';
import { fontFamily } from '../../../../app-core/app-theme';
import { IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { distinct, forEach } from '../../../../framework/utils/collection';

export const PRODUCT_DETAIL_VARIANT_SELECTOR_ID = 'prd-dtl-var-sel';

interface IProductDetailPanelProps extends IProductDetailPanelDisplayProps {
  imageUrls: string[];
  initialVariantSelection: BwjEventArgs<IProductTexture | null>;
  showPanel: boolean;
  onClosePanel: () => void;
}

const getStyleClasses = (theme: ITheme) => {
  const root: IStyle = {
    fontFamily: fontFamily,
    padding: '0 1.5rem',
  };

  const contentContainer: IStyle = {
    maxWidth: 1140,
    minWidth: 300,
    margin: '0 auto',
  };

  return mergeStyleSets({
    root,
    contentContainer,
  });
}

export const ProductDetailPanel: React.FunctionComponent<IProductDetailPanelProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);

  const { initialVariantSelection, variants, imageUrls, title } = props;
  const [selectedVariant, setSelectedVariant] = useState<IProductTexture | null>(null);
  const [variantOptionsVisible, setVariantOptionsVisible] = useState(true);
  const [invokePanelScrollTo, setInvokePanelScrollTo] = useState<BwjEventArgs<{selector: string, offset?: number}> | undefined>(undefined);
  const [verticalScrollInfo, setVerticalScrollInfo] = useState({ scrollY: 0, vpHeight: 0, containerSelector: '' });
  const [contentWidth, setContentWidth] = useState(0);
  const [displayedImageIndex, setDisplayedImageIndex] = useState(0);
  const images = useMemo(() => {
    const imgs: IBrowserImage[] = !!selectedVariant ? selectedVariant.productImageUrls.map(src => {
      return { src, alt: title };
    }) : [];

    forEach(imageUrls, src => {
      imgs.push({ src, alt: title });
    });

    const uniqueImgs = distinct(imgs, i => i.src);
    return uniqueImgs;
  }, [imageUrls, selectedVariant, title]);

  useEffect(() => {
    if(!initialVariantSelection || initialVariantSelection.inaccessible()) { return; }

    const i = initialVariantSelection.getArgs();
    const ivs = variants.length === 1 ? variants[0] : i;
    setSelectedVariant(ivs);
  }, [initialVariantSelection, variants]);

  const onFooterSelectVariantClicked = () => {
    setInvokePanelScrollTo(new BwjEventArgs({ selector: PRODUCT_DETAIL_VARIANT_SELECTOR_ID, offset: 10 }));
  };

  const onScrollChanged = (
    scrollTop: number,
    scrollLeft: number,
    containerWidth: number,
    containerHeight: number,
    scrollContainerSelector: string) => {
    setVerticalScrollInfo({
      scrollY: scrollTop,
      vpHeight: containerHeight,
      containerSelector: '#' + scrollContainerSelector,
    });
  };

  const onWidthChange = useCallback((width: number) => setContentWidth(width), []);

  const onVariantSelected = (variant: IProductTexture) => {
    setSelectedVariant(variant);
    setDisplayedImageIndex(0);
  }

  return (
    <BwjSidePanel
      showPanel={props.showPanel}
      panelType={PanelType.large}
      onDismiss={props.onClosePanel}
      closeButtonAriaLabel={'Close product details dialog'}
      customFooter={(<ProductDetailPanelFooter
        title={title}
        averageRating={props.averageRating}
        rating={props.rating}
        selectedVariant={selectedVariant}
        price={props.price}
        actionButtonText={props.chooseProductButtonText}
        onActionButtonClicked={() => {
          !!selectedVariant && props.onProductChosen(selectedVariant);
        }}
        variantDescriptor={props.variantDescriptor}
        variantOptionsVisible={variantOptionsVisible}
        selectVariantButtonText={props.chooseVariantButtonText}
        onSelectVariantClicked={onFooterSelectVariantClicked}
      />)}
      invokeScrollTo={invokePanelScrollTo}
      onScrollChanged={onScrollChanged}
    >
      <div className={cs.root}>
        <BwjConscientiousContainer
          className={cs.contentContainer}
          pollingFrequency={1200}
          onWidthChange={onWidthChange}
        >
          <ProductDetailPanelContent
            id={props.id}
            brand={props.brand}
            title={title}
            price={props.price}
            averageRating={props.averageRating}
            rating={props.rating}
            keyFeatures={props.keyFeatures}
            includedServices={props.includedServices}
            imageUrls={images}
            displayedImageIndex={displayedImageIndex}
            onDisplayedIndexChanged={setDisplayedImageIndex}
            variants={variants}
            variantDescriptor={props.variantDescriptor}
            recommendation={props.recommendation}
            priceChange={props.priceChange}
            chooseProductButtonText={props.chooseProductButtonText}
            onProductChosen={props.onProductChosen}
            chooseVariantButtonText={props.chooseVariantButtonText}
            selectedVariant={selectedVariant}
            onVariantSelected={onVariantSelected}
            onVariableOptionsVisibilityChange={setVariantOptionsVisible}
            verticalScrollInfo={verticalScrollInfo}
            contentWidth={contentWidth}
            onIncludedServicesOverlayRequested={props.onIncludedServicesOverlayRequested}
            onSeeInPersonRequested={props.onSeeInPersonRequested}
          />
        </BwjConscientiousContainer>
      </div>
    </BwjSidePanel>
  );
};
